import { useState, useEffect, useCallback } from 'react'
import { adminApi } from '../api/admin'
import { getErrorMessage } from '@/shared/api/client'
import { useAuthStore } from '@/features/auth/stores/authStore'
import type { MemberResponse, PermissionJSON, UserResponse } from '@/shared/api/generated'

type PermKey = keyof PermissionJSON

const PERM_COLUMNS: { key: PermKey; label: string }[] = [
  { key: 'can_delete', label: 'Delete' },
  { key: 'can_upload', label: 'Upload' },
  { key: 'can_download', label: 'Download' },
  { key: 'can_list', label: 'List' },
  { key: 'can_rename', label: 'Rename' },
  { key: 'can_manage_vals', label: 'Manage Values' },
]

interface StagedChange {
  role?: string
  perms?: Partial<PermissionJSON>
}

export function GroupMembers({ groupId, groupName }: { groupId: number; groupName: string }) {
  const currentUser = useAuthStore((s) => s.user)
  const [members, setMembers] = useState<MemberResponse[]>([])
  const [allUsers, setAllUsers] = useState<UserResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [staged, setStaged] = useState<Map<number, StagedChange>>(new Map())
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())
  const [addUsername, setAddUsername] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [m, u] = await Promise.all([
        adminApi.getGroupMembers(groupId),
        adminApi.listAllUsers(),
      ])
      setMembers(m)
      setAllUsers(u)
      setStaged(new Map())
      setRemovedIds(new Set())
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => { load() }, [load])

  const hasChanges = staged.size > 0 || removedIds.size > 0

  const getEffective = (m: MemberResponse): { role: string; perms: PermissionJSON } => {
    const s = staged.get(m.user_id)
    return {
      role: s?.role ?? m.role,
      perms: {
        can_delete: s?.perms?.can_delete ?? m.can_delete,
        can_upload: s?.perms?.can_upload ?? m.can_upload,
        can_download: s?.perms?.can_download ?? m.can_download,
        can_list: s?.perms?.can_list ?? m.can_list,
        can_rename: s?.perms?.can_rename ?? m.can_rename,
        can_manage_vals: s?.perms?.can_manage_vals ?? m.can_manage_vals,
      },
    }
  }

  const stagePermChange = (userId: number, key: PermKey, value: boolean) => {
    setStaged((prev) => {
      const next = new Map(prev)
      const existing = next.get(userId) || {}
      next.set(userId, { ...existing, perms: { ...existing.perms, [key]: value } })
      return next
    })
  }

  const stageRoleChange = (userId: number, toAdmin: boolean) => {
    setStaged((prev) => {
      const next = new Map(prev)
      const existing = next.get(userId) || {}
      next.set(userId, { ...existing, role: toAdmin ? 'admin' : 'member' })
      return next
    })
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      for (const userId of removedIds) {
        await adminApi.removeGroupMember(groupId, userId)
      }
      for (const [userId, change] of staged) {
        if (removedIds.has(userId)) continue
        if (change.role) await adminApi.updateMemberRole(groupId, userId, change.role)
        if (change.perms) {
          const member = members.find((m) => m.user_id === userId)!
          await adminApi.updateMemberPermissions(groupId, userId, {
            can_delete: change.perms.can_delete ?? member.can_delete,
            can_upload: change.perms.can_upload ?? member.can_upload,
            can_download: change.perms.can_download ?? member.can_download,
            can_list: change.perms.can_list ?? member.can_list,
            can_rename: change.perms.can_rename ?? member.can_rename,
            can_manage_vals: change.perms.can_manage_vals ?? member.can_manage_vals,
          })
        }
      }
      await load()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const addMember = async () => {
    if (!addUsername) return
    setError('')
    try {
      await adminApi.addGroupMember(groupId, addUsername, 'member')
      setAddUsername('')
      await load()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const memberUserIds = new Set(members.map((m) => m.user_id))
  const availableUsers = allUsers.filter((u) => !u.deleted && !memberUserIds.has(u.id))

  return (
    <>
      <div className="card-header">
        <h2>{groupName} Members</h2>
        {hasChanges && (
          <div className="button-row">
            <button className="btn-secondary" onClick={() => { setStaged(new Map()); setRemovedIds(new Set()) }} disabled={saving}>Discard</button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {loading ? (
        <div className="empty-state">Loading members...</div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th className="centered">Admin</th>
                  {PERM_COLUMNS.map((col) => (
                    <th key={col.key} className="centered">{col.label}</th>
                  ))}
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const { role, perms } = getEffective(member)
                  const isChanged = staged.has(member.user_id)
                  const isRemoved = removedIds.has(member.user_id)
                  const isSelf = currentUser?.id === member.user_id
                  const cls = isRemoved ? 'removed' : isChanged ? 'changed' : undefined
                  return (
                    <tr key={member.user_id} className={cls}>
                      <td>{member.username}</td>
                      <td className="centered">
                        <input type="checkbox" checked={role === 'admin'}
                          onChange={(e) => stageRoleChange(member.user_id, e.target.checked)}
                          disabled={isSelf || isRemoved} />
                      </td>
                      {PERM_COLUMNS.map((col) => (
                        <td key={col.key} className="centered">
                          <input type="checkbox" checked={perms[col.key]}
                            onChange={(e) => stagePermChange(member.user_id, col.key, e.target.checked)}
                            disabled={isRemoved} />
                        </td>
                      ))}
                      <td className="right">
                        {!isSelf && (
                          isRemoved
                            ? <button className="link success" onClick={() => setRemovedIds((p) => { const n = new Set(p); n.delete(member.user_id); return n })}>Undo</button>
                            : <button className="link danger" onClick={() => setRemovedIds((p) => new Set(p).add(member.user_id))}>Remove</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="add-member-bar">
            <select value={addUsername} onChange={(e) => setAddUsername(e.target.value)}>
              <option value="">Select user...</option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.username}>{u.username}</option>
              ))}
            </select>
            <button className="btn-success" onClick={addMember} disabled={!addUsername}>Add Member</button>
          </div>
        </>
      )}
    </>
  )
}
